import { ClientReconnectControls } from './ClientReconnectControls';
// Client detail at ≤880px (mobile overhaul Phase C §7.1 B/C) — the same
// split ProjectDetailMobile uses: it owns no data, pages/ClientDetail.tsx
// keeps every fetch, handler and modal and hands them down, so both renders
// always PATCH the same way. The desktop rail is not rendered at this width;
// its rows come back as a FieldRow list under "Details".

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CLIENT_STATUSES,
  LEAD_STATUS_LABELS,
  PHASE_LABELS,
  type ClientContactRow,
  type ClientRow,
  type ClientStatus,
  type InvoiceRow,
  type LeadStatus,
  type ProjectPhase,
  type ProjectRow,
} from '../../shared/types';
import { DASHBOARD_BASE } from '../lib/projectDetailShared';
import { formatDate, formatDayMonth, formatProjectNumber } from '../lib/calendar';
import { formatInvoiceNumber } from '../../shared/format';
import { formatMoney } from '../lib/moneyFormat';
import { Modal, NotesBlock, Pill } from './primitives';
import { BackIcon, DotsIcon } from './icons';
import { ContactSheet } from './ContactSheet';
import { FieldRow, FieldSheet, type FieldKind } from './FieldSheet';
import { PageMenu } from './PageMenu';
import { PickerSheet } from './PickerSheet';

const STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Lead',
  active: 'Active',
  past: 'Past',
  archived: 'Archived',
};

const PHASE_DOT_CLASS: Partial<Record<ProjectPhase, string>> = {
  'lead': 'pd-lead',
  'pre-production': 'pd-pre',
  'shoot': 'pd-shoot',
  'post': 'pd-post',
  'clean_up': 'pd-cleanup',
  'on_hold': 'pd-hold',
};

type ClientProject = ProjectRow & { open_tasks: number };
type ClientInvoice = InvoiceRow & { project_name: string | null; project_voided_at: number | null };
type ClientLead = { id: string; name: string; status: LeadStatus; enquiry_date: string | null };

/** Editable client details that open a single-field sheet. */
type EditField = { key: 'website' | 'address' | 'dashboard_slug' | 'payment_terms_days'; title: string; kind: FieldKind; value: string };

/** Section header — "Projects · 25 · Show all →". Shared with the Clients
    list's group headers so both read as one system. */
function GroupHead({ title, count, link }: { title: string; count?: ReactNode; link?: ReactNode }) {
  return (
    <div className="client-group">
      <span>{title}</span>
      {count !== undefined && <span className="n">{count}</span>}
      {link}
    </div>
  );
}

export function ClientDetailMobile({
  client,
  projects,
  invoices,
  leads,
  contacts,
  billed,
  outstanding,
  overdueCount,
  activeCount,
  firstProject,
  today,
  displayInvoice,
  invoiceState,
  shortDate,
  patch,
  addContact,
  patchContact,
  deleteContact,
  onNewProject,
  onNewInvoice,
  onCopyPrefs,
  prefsLinkBusy,
  prefsLinkNote,
  onTrash,
}: {
  client: ClientRow;
  projects: ClientProject[];
  invoices: ClientInvoice[];
  leads: ClientLead[];
  contacts: ClientContactRow[];
  billed: number;
  outstanding: number;
  overdueCount: number;
  activeCount: number;
  /** "Mar 2023", or "—" when the client has no dated project yet. */
  firstProject: string;
  today: string;
  displayInvoice: (invoice: ClientInvoice) => number;
  invoiceState: (invoice: InvoiceRow, today: string) => { text: string; cls: string };
  shortDate: (iso: string | null, today: string) => string;
  patch: (fields: Record<string, unknown>) => Promise<void>;
  addContact: (fields: Record<string, unknown>) => Promise<void>;
  patchContact: (contactId: string, fields: Record<string, unknown>) => Promise<void>;
  deleteContact: (contactId: string) => Promise<void>;
  onNewProject: () => void;
  onNewInvoice: () => void;
  onCopyPrefs: () => Promise<void>;
  prefsLinkBusy: boolean;
  /** "Copied!" — the transient note the page already owns. */
  prefsLinkNote: string | null;
  onTrash: () => Promise<void>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [contactEdit, setContactEdit] = useState<ClientContactRow | 'new' | null>(null);
  const [field, setField] = useState<EditField | null>(null);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashBusy, setTrashBusy] = useState(false);
  const [trashError, setTrashError] = useState<string | null>(null);

  const defaultContact = contacts.find((c) => c.is_default === 1) ?? null;
  const visibleProjects = showAllProjects ? projects : projects.slice(0, 4);
  const visibleLeads = showAllLeads ? leads : leads.slice(0, 4);
  const visibleInvoices = invoices.slice(0, 6);

  const openField = (key: EditField['key'], title: string, kind: FieldKind) =>
    setField({ key, title, kind, value: String(client[key] ?? '') });

  const confirmTrash = async () => {
    if (trashBusy) return;
    setTrashBusy(true);
    setTrashError(null);
    try {
      await onTrash();
    } catch (err) {
      // 409 when projects/invoices/leads are still attached — say so in the
      // sheet instead of silently doing nothing (desktop's TrashAction does
      // the same via reportError).
      setTrashError(err instanceof Error ? err.message : 'Could not move the client to trash.');
      setTrashBusy(false);
    }
  };

  return (
    <div className="page-body">
      <div className="content wide cd-m">
        <div className="cd-head">
          <div className="cd-head-row">
            <Link className="cd-back" to="/clients">
              <BackIcon />
              Clients
            </Link>
            <button
              type="button"
              className={`ib${menuOpen ? ' on' : ''}`}
              aria-label="Client actions"
              onClick={() => setMenuOpen(true)}
            >
              <DotsIcon />
            </button>
          </div>
          <h1 className="cd-title">{client.name}</h1>
          <div className="cd-sub">
            {firstProject !== '—' && <>Client since {firstProject} · </>}
            {projects.length} project{projects.length === 1 ? '' : 's'}
            {billed > 0 && (
              <>
                {' · '}
                <span className="gold">{formatMoney(billed)}</span> billed all time
              </>
            )}
          </div>
          <div className="cd-pills">
            <button type="button" className="cd-pill-btn" aria-label="Change status" onClick={() => setStatusOpen(true)}>
              <Pill tone="accent">
                {STATUS_LABELS[client.status]} <span className="caret">▾</span>
              </Pill>
            </button>
            {outstanding > 0 && (
              <span className={`cd-out${overdueCount > 0 ? ' overdue' : ''}`}>
                {formatMoney(outstanding)} outstanding
                {overdueCount > 0 && ` · ${overdueCount} overdue`}
              </span>
            )}
          </div>
        </div>

        <div className="cd-actions">
          <button type="button" className="cbtn wide pri" onClick={onNewInvoice}>
            + Invoice
          </button>
          <button type="button" className="cbtn wide" onClick={onNewProject}>
            + Project
          </button>
          {client.dashboard_slug && (
            <a
              className="cbtn cd-dash"
              href={`${DASHBOARD_BASE}/${client.dashboard_slug}`}
              target="_blank"
              rel="noreferrer"
            >
              Dashboard ↗
            </a>
          )}
        </div>

        <GroupHead
          title="Projects"
          count={projects.length}
          link={
            projects.length > 4 &&
            !showAllProjects && (
              <button className="client-group-link" onClick={() => setShowAllProjects(true)}>
                Show all →
              </button>
            )
          }
        />
        {visibleProjects.map((p) => {
          const delivered = p.phase === 'delivered' || p.phase === 'clean_up' || p.phase === 'closed';
          const number = formatProjectNumber(p.project_number);
          return (
            <Link className="client-row" key={p.id} to={`/projects/${p.id}`}>
              <span
                className={`phase-dot ${PHASE_DOT_CLASS[p.phase] ?? ''}`}
                style={delivered && p.phase !== 'clean_up' ? { background: 'var(--ok)' } : undefined}
              />
              <span className="client-col">
                <span className="client-row-title">{p.name}</span>
                <span className="client-row-meta">
                  {!delivered && p.open_tasks > 0 && (
                    <>
                      <span className="client-row-live">
                        {p.open_tasks} open task{p.open_tasks === 1 ? '' : 's'}
                      </span>
                      <span className="client-sep">·</span>
                    </>
                  )}
                  <Pill tone={p.phase === 'post' ? 'accent' : p.phase === 'shoot' ? 'gold' : p.phase === 'clean_up' ? 'cleanup' : undefined}>
                    {PHASE_LABELS[p.phase]}
                  </Pill>
                </span>
              </span>
              <span className="client-right">
                {number && <span>{number}</span>}
                <span>
                  {!delivered && p.due_date ? 'due ' : ''}
                  {shortDate(delivered ? p.delivered_date : p.due_date, today)}
                </span>
              </span>
            </Link>
          );
        })}
        {projects.length === 0 && <div className="empty-note">No projects yet.</div>}

        <GroupHead
          title="Invoices"
          count={invoices.length}
          link={
            <Link className="client-group-link" to="/finance">
              All in Finance →
            </Link>
          }
        />
        {visibleInvoices.map((inv) => {
          const state = invoiceState(inv, today);
          return (
            <Link className="client-row" key={inv.id} to={`/finance/invoices/${inv.id}`}>
              <span className="client-col">
                <span className="client-row-title mono">{formatInvoiceNumber(inv.invoice_number)}</span>
                <span className="client-row-meta">
                  {inv.project_name && (
                    <>
                      <span className="client-row-live">{inv.project_name}</span>
                      <span className="client-sep">·</span>
                    </>
                  )}
                  <span className={`inv-state${state.cls}`}>{inv.status === 'draft' ? 'not sent' : state.text}</span>
                </span>
              </span>
              <span className="client-right">
                <span className="gold">{formatMoney(displayInvoice(inv))}</span>
              </span>
            </Link>
          );
        })}
        {invoices.length === 0 && <div className="empty-note">No invoices yet.</div>}

        {leads.length > 0 && (
          <>
            <GroupHead
              title="Leads"
              count={leads.length}
              link={
                leads.length > 4 &&
                !showAllLeads && (
                  <button className="client-group-link" onClick={() => setShowAllLeads(true)}>
                    Show all →
                  </button>
                )
              }
            />
            {visibleLeads.map((lead) => (
              <Link className="client-row" key={lead.id} to={`/leads/${lead.id}`}>
                <span className="client-col">
                  <span className="client-row-title">{lead.name}</span>
                  <span className="client-row-meta">
                    <span className="client-row-live">
                      {lead.enquiry_date ? `enq ${formatDayMonth(lead.enquiry_date)}` : '—'}
                    </span>
                  </span>
                </span>
                <span className="client-right">
                  <Pill tone={lead.status === 'won' || lead.status === 'locked_in' ? 'ok' : undefined}>{LEAD_STATUS_LABELS[lead.status]}</Pill>
                </span>
              </Link>
            ))}
          </>
        )}

        <GroupHead
          title="Contacts"
          count={contacts.length}
          link={
            <button className="client-group-link" onClick={() => setContactEdit('new')}>
              + Add contact
            </button>
          }
        />
        {contacts.map((contact) => (
          <button type="button" className="client-row" key={contact.id} onClick={() => setContactEdit(contact)}>
            <span className="client-mark">{contact.name.slice(0, 1).toUpperCase()}</span>
            <span className="client-col">
              <span className="client-row-title">
                {contact.name}
                {contact.is_default === 1 && (
                  <span className="cd-default">
                    <Pill tone="accent">Default</Pill>
                  </span>
                )}
              </span>
              <span className="client-row-meta">
                <span className="client-row-live">
                  {[contact.role_label, contact.email || contact.phone].filter(Boolean).join(' · ') || '—'}
                </span>
              </span>
            </span>
            <span className="chev">›</span>
          </button>
        ))}
        {contacts.length === 0 && <div className="empty-note">No contacts yet.</div>}

        <NotesBlock notes={client.notes} onSave={(notes) => void patch({ notes })} />

        <GroupHead title="Details" />
        <FieldRow label="Status" value={STATUS_LABELS[client.status]} onPress={() => setStatusOpen(true)} />
        <FieldRow
          label="Default contact"
          tone="acc"
          value={
            defaultContact
              ? [defaultContact.name, defaultContact.role_label].filter(Boolean).join(' · ')
              : 'No contacts yet'
          }
        />
        <FieldRow
          label="Website"
          mono
          tone="acc"
          value={client.website}
          empty={!client.website}
          onPress={() => openField('website', 'Website', 'url')}
        />
        <FieldRow
          label="Address"
          value={client.address}
          empty={!client.address}
          onPress={() => openField('address', 'Address', 'text')}
        />
        <FieldRow
          label="Payment terms (days)"
          mono
          value={client.payment_terms_days ?? 'Default'}
          onPress={() => openField('payment_terms_days', 'Payment terms (days)', 'number')}
        />
        <FieldRow
          label="Client dashboard"
          mono
          tone="acc"
          value={client.dashboard_slug ? `/${client.dashboard_slug}` : null}
          empty={!client.dashboard_slug}
          onPress={() => openField('dashboard_slug', 'Client dashboard', 'text')}
        />
        <div style={{padding:'0 20px 16px'}}><ClientReconnectControls id={client.id} name={client.name} blocked={!!client.do_not_contact}/></div>
        <FieldRow
          label="Email preferences"
          value={
            <span className="cd-prefs">
              {([
                ['notify_invoices', 'Invoices'],
                ['notify_nudges', 'Nudges'],
                ['notify_milestones', 'Milestones'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`pill${client[key] ? ' accent' : ''}`}
                  aria-label={`${label} notifications`}
                  aria-pressed={!!client[key]}
                  disabled={prefsBusy}
                  onClick={async () => {
                    setPrefsBusy(true);
                    try { await patch({ [key]: client[key] ? 0 : 1 }); }
                    finally { setPrefsBusy(false); }
                  }}
                >
                  {label} {client[key] ? 'on' : 'off'}
                </button>
              ))}
            </span>
          }
        />
        <FieldRow label="Billed all time" mono tone="gold" value={formatMoney(billed)} />
        <FieldRow
          label="Outstanding"
          mono
          tone="gold"
          value={
            <>
              {formatMoney(outstanding)}
              {overdueCount > 0 && <span className="cd-overdue"> · {overdueCount} OVERDUE</span>}
            </>
          }
        />
        <FieldRow label="Projects" mono value={`${projects.length} total · ${activeCount} active`} />
        <FieldRow label="First project" mono value={firstProject} />

        <div className="cd-stamp">
          <span>Created {formatDate(new Date(client.created_at).toISOString().slice(0, 10))}</span>
          <span>Updated {formatDate(new Date(client.updated_at).toISOString().slice(0, 10))}</span>
        </div>

        {prefsLinkNote && (
          <div className="cd-toast" role="status">
            {prefsLinkNote}
          </div>
        )}

        <PageMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title={client.name}
          items={[
            {
              key: 'prefs',
              label: 'Copy preferences link',
              sublabel: 'Lets the client set their own email preferences',
              disabled: prefsLinkBusy,
              onSelect: () => void onCopyPrefs(),
            },
            { key: 'trash', label: 'Move client to trash', tone: 'danger', onSelect: () => setTrashOpen(true) },
          ]}
        />

        <PickerSheet
          open={statusOpen}
          title="Status"
          items={CLIENT_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))}
          value={client.status}
          onSelect={(status) => void patch({ status })}
          onClose={() => setStatusOpen(false)}
        />

        {field && (
          <FieldSheet
            open
            title={field.title}
            context={field.key === 'payment_terms_days' ? 'Used for the due date on new invoices' : client.name}
            placeholder={field.key === 'payment_terms_days' ? 'Default' : undefined}
            kind={field.kind}
            value={field.value}
            onSave={(raw) => void patch({ [field.key]: raw.trim() === '' ? null : field.key === 'payment_terms_days' ? Number(raw) : raw.trim() })}
            onClose={() => setField(null)}
          />
        )}

        {contactEdit && (
          <ContactSheet
            contact={contactEdit === 'new' ? undefined : contactEdit}
            // A sole default can still be deleted — only a default with
            // siblings is blocked, exactly as the desktop row decides.
            deleteBlocked={contactEdit !== 'new' && contactEdit.is_default === 1 && contacts.length > 1}
            onSave={(fields) =>
              contactEdit === 'new' ? addContact(fields) : patchContact(contactEdit.id, fields)
            }
            onDelete={contactEdit === 'new' ? undefined : () => deleteContact(contactEdit.id)}
            onClose={() => setContactEdit(null)}
          />
        )}

        {trashOpen && (
          <Modal
            mobile="half"
            title="Move client to trash?"
            context={client.name}
            onClose={() => {
              if (!trashBusy) setTrashOpen(false);
            }}
            foot={
              <>
                <button className="btn" disabled={trashBusy} onClick={() => setTrashOpen(false)}>
                  Cancel
                </button>
                <button className="btn primary danger" disabled={trashBusy} onClick={() => void confirmTrash()}>
                  {trashBusy ? 'Moving…' : 'Move to trash'}
                </button>
              </>
            }
          >
            <p className="cd-confirm">
              It goes to Trash for 30 days. A client with projects, invoices, leads or tasks still attached can't be
              removed.
            </p>
            {trashError && (
              <p className="error-text" role="alert">
                {trashError}
              </p>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
}
